/**
 * Privacy Opt-Out Manager
 * Handles GPC detection, localStorage opt-out management, and Shopify consent API integration
 * CCPA/US State Privacy Law compliant
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'privacy_opt_out';
  // Shopify's consent record as of the visitor's most recent decision or
  // fresh page load -- see reloadIfConsentStale().
  const LATEST_RECORD_KEY = 'consent_latest_record';
  const OPT_OUT_EVENT = 'privacyOptOutReady';
  const OPT_OUT_CHANGE_EVENT = 'privacyOptOutChange';

  // Debug logging gate. This module runs on every pageview for every visitor,
  // so its console output (which includes customerId/email) is opt-in only.
  // Enable with ?consent_debug=1, disable with ?consent_debug=0; the choice
  // persists in sessionStorage for the rest of the tab session.
  let DEBUG = false;
  try {
    const dbgParam = new URLSearchParams(location.search).get('consent_debug');
    if (dbgParam === '1') sessionStorage.setItem('consent_debug', '1');
    else if (dbgParam === '0') sessionStorage.removeItem('consent_debug');
    DEBUG = sessionStorage.getItem('consent_debug') === '1';
  } catch (e) {}

  function log(...args) { if (DEBUG) console.log(...args); }
  function warn(...args) { if (DEBUG) console.warn(...args); }

  const PrivacyOptOut = {
    customerId: null,
    isOptedOut: false,
    hasGPCSignal: false,
    shopifyOptOut: false,
    _initialized: false,

    /**
     * Initialize the privacy opt-out manager.
     *
     * Called TWICE by design: once synchronously from <head> (theme.liquid), so
     * opt-out state and Shopify reconciliation happen as early as possible, and
     * again on DOMContentLoaded (also theme.liquid, site-wide). The head call
     * runs before <body> exists, so it can never bind the privacy-page form --
     * that's what the second call is for. Everything else must run only once,
     * hence the _initialized guard (a second full run would double-bind the
     * form and fire duplicate app-proxy POSTs on submit).
     *
     * @param {string|null} customerId - Shopify customer ID if logged in
     */
    init(customerId) {
      if (this._initialized) {
        // Re-entry (privacy-choices page): only (re)bind DOM that didn't exist
        // on the first pass. bindFormHandlers() is itself idempotent per element.
        this.bindFormHandlers();
        return;
      }
      this._initialized = true;

      this.customerId = customerId;
      this.hasGPCSignal = this.detectGPC();

      // Clear localStorage if customer changed (logged out or different user)
      this.clearIfCustomerChanged(customerId);

      this.shopifyOptOut = this.getShopifyOptOutStatus();

      // Sync from server tag if customer is tagged but localStorage doesn't reflect it
      if (window.__customerPrivacy && window.__customerPrivacy.hasPrivacyTag && !this.getOptOutStatus()) {
        log('[PrivacyOptOut] Server tag detected, syncing to localStorage');
        this.setOptOut(true, 'server_tag');
      }

      // Check localStorage, GPC, and Shopify customer privacy status
      this.isOptedOut = this.getOptOutStatus() || this.shopifyOptOut;

      // Auto opt-out if GPC signal is detected. Safe to do before the API is
      // ready: GPC is a standing legal opt-out that no stored state can revoke.
      if (this.hasGPCSignal && !this.isOptedOut) {
        this.setOptOut(true, 'gpc');
      }

      // Reconcile against Shopify — but ONLY once its API is actually available.
      //
      // This MUST be deferred. init() runs from a <head> script that executes
      // well before Shopify.customerPrivacy exists (confirmed in production:
      // "customerPrivacy API not available, retrying (1/5)" and
      // "nativeApiReady: false, region: 'n/a'" both logged during init()).
      // Reading the record synchronously here returns null, so the reconcile
      // was skipped entirely -- and then the unconditional push below fired
      // with our STALE localStorage value, whose own retry loop happily waited
      // for the API and overwrote whatever the visitor had chosen in Shopify's
      // preferences modal. That asymmetry (sync retried, reconcile didn't) is
      // what kept reverting "Accept all" on the next navigation.
      //
      // The staleness check must come first: on a stale page, reconcile would
      // push our stored opt-out into Shopify's outdated record, and
      // rememberShopifyRecord() would overwrite the real latest decision.
      const isHistoryLoad = this.navigationType() === 'back_forward';
      this.whenShopifyPrivacyReady(() => {
        if (isHistoryLoad) {
          if (this.reloadIfConsentStale(this.readShopifyRecord(), 'history load')) return;
        } else {
          // A normal navigation or reload comes from the server, so its
          // record is current by definition.
          this.rememberShopifyRecord();
        }
        this.reconcileWithShopify();
      });

      // Capture decisions made through Shopify's OWN UI (native banner buttons
      // and the preferences modal). Without this, a decline in a consent-required
      // region is recorded by Shopify but never mirrored into our localStorage,
      // so on the next load getOptOutStatus() reads false and the privacy page
      // tells a visitor who just declined that they have "not opted out".
      this.bindNativeConsentListener();

      // A back/forward-cache restore re-shows whatever consent state the page
      // was left in, without re-running any of this. See reloadIfConsentStale().
      this.reloadStaleBfcacheRestores();

      // Dispatch ready event
      this.dispatchEvent(OPT_OUT_EVENT, {
        isOptedOut: this.isOptedOut,
        hasGPCSignal: this.hasGPCSignal,
        shopifyOptOut: this.shopifyOptOut,
        customerId: this.customerId
      });

      // Bind form handlers if on privacy page
      this.bindFormHandlers();

      log('[PrivacyOptOut] Initialized', {
        isOptedOut: this.isOptedOut,
        hasGPCSignal: this.hasGPCSignal,
        shopifyOptOut: this.shopifyOptOut,
        customerId: this.customerId
      });

      // Log initial Shopify consent state for debugging
      this.verifyShopifyConsent();
    },

    /**
     * Clear localStorage if a DIFFERENT customer logs in
     * Keeps preferences when: same user logs out/in, or guest browsing
     * @param {string|null} currentCustomerId - Current customer ID
     */
    clearIfCustomerChanged(currentCustomerId) {
      try {
        const storedData = this.getOptOutData();
        if (!storedData) return;

        const storedCustomerId = storedData.customerId;

        // Only clear if a DIFFERENT user is now logged in
        // Don't clear if:
        // - Same user logged back in (currentCustomerId === storedCustomerId)
        // - User logged out (currentCustomerId is null) - keep preference for when they log back in
        // - Guest opted out (storedCustomerId is null) - keep guest preference
        if (currentCustomerId !== null &&
            storedCustomerId !== null &&
            storedCustomerId !== currentCustomerId) {
          log('[PrivacyOptOut] Different customer logged in, clearing stored preferences');
          log('  - Stored customer:', storedCustomerId);
          log('  - Current customer:', currentCustomerId);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_KEY + '_request');
        } else {
          log('[PrivacyOptOut] Keeping stored preferences', {
            storedCustomerId,
            currentCustomerId,
            isOptedOut: storedData.optedOut
          });
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error checking customer change:', e);
      }
    },

    /**
     * Detect Global Privacy Control (GPC) signal
     * @returns {boolean}
     */
    detectGPC() {
      return navigator.globalPrivacyControl === true;
    },

    /**
     * Run cb once Shopify.customerPrivacy is available.
     *
     * init() fires from a <head> script, long before Shopify's consent API is
     * loaded, so anything that READS Shopify's record has to wait for it. Runs
     * cb immediately when the API is already present.
     *
     * Polls at a short fixed interval rather than backing off: a growing gap
     * meant noticing the API up to ~1s after it loaded, delaying the stale-page
     * reload (reloadIfConsentStale) and everything after it. Same ~16s budget.
     *
     * @param {Function} cb
     */
    whenShopifyPrivacyReady(cb) {
      const POLL_MS = 50;
      const GIVE_UP_MS = 16500;
      const apiReady = () => typeof window.Shopify !== 'undefined' && !!window.Shopify.customerPrivacy;

      if (apiReady()) {
        cb();
        return;
      }

      const startedAt = Date.now();
      const timer = setInterval(() => {
        if (apiReady()) {
          clearInterval(timer);
          cb();
          return;
        }
        if (Date.now() - startedAt < GIVE_UP_MS) return;

        clearInterval(timer);
        warn('[PrivacyOptOut] customerPrivacy API never became available; ' +
             'skipping reconcile (leaving stored state untouched)');
      }, POLL_MS);
    },

    /**
     * Reconcile our stored state against Shopify's, once its API is ready.
     *
     * Shopify's record WINS whenever it holds an explicit decision: its
     * preferences modal writes straight to Shopify and never routes through
     * setOptOut(), so our localStorage can be stale by a full decision. Our
     * storage is a MIRROR, never an authority that overwrites a newer choice.
     *
     * Only pushes to Shopify when it has NO decision of its own AND we hold a
     * real opt-out to enforce (GPC / server tag / stored decline). Pushing when
     * Shopify already agrees is a redundant write on every pageview, and
     * pushing when it disagrees is precisely the bug this replaces.
     */
    reconcileWithShopify() {
      const shopifyDecision = this.getShopifyDecision();

      // A standing GPC/server-tag opt-out outranks whatever Shopify stored --
      // those can't be revoked by a UI click, so enforce them if they disagree.
      const standingOptOut = this.hasGPCSignal ||
        !!(window.__customerPrivacy && window.__customerPrivacy.hasPrivacyTag);

      if (standingOptOut) {
        this.isOptedOut = true;
        if (shopifyDecision !== true) {
          log('[PrivacyOptOut] Enforcing standing opt-out (GPC/server tag)');
          this.syncWithShopifyConsent();
        }
        this.updatePrivacyUI();
        return;
      }

      if (shopifyDecision !== null) {
        // Shopify has an explicit decision -- adopt it, never overwrite it.
        if (shopifyDecision !== this.isOptedOut) {
          log('[PrivacyOptOut] Shopify record differs from stored copy; Shopify wins', {
            stored: this.isOptedOut,
            shopify: shopifyDecision
          });
          this.writeOptOutRecord(shopifyDecision, 'shopify_reconcile');
          this.dispatchEvent(OPT_OUT_CHANGE_EVENT, {
            isOptedOut: shopifyDecision,
            source: 'shopify_reconcile'
          });
        }
        this.updatePrivacyUI();
        return;
      }

      // Shopify has no decision on record. Push ours only if it's a real
      // opt-out; never write a positive consent for an undecided visitor (that
      // would silently opt them in and suppress the native banner, which is
      // the bug this whole gate originally fixed).
      if (this.isOptedOut) {
        this.syncWithShopifyConsent();
      }
      this.updatePrivacyUI();
    },

    /**
     * Read Shopify's EXPLICIT consent decision, if one exists.
     *
     * @returns {boolean|null} true = opted out, false = opted in,
     *   null = no decision recorded yet (all categories blank), so the caller
     *   should fall back to its own state rather than inventing one.
     */
    getShopifyDecision() {
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.currentVisitorConsent !== 'function') return null;
        const c = cp.currentVisitorConsent();
        if (!c || typeof c !== 'object') return null;

        // Consider ONLY the categories the visitor was actually asked about.
        //
        // sale_of_data must NOT be lumped in with marketing/analytics. In
        // GDPR-style regions (Quebec, EU) Shopify's banner and preferences
        // modal expose Required / Personalization / Marketing / Analytics --
        // there is no sale-of-data toggle at all -- so sale_of_data stays "no"
        // (or blank) no matter what the visitor chooses. Treating that "no" as
        // a decline made "Accept all" read as an opt-out, which then pushed a
        // denial back over the visitor's own acceptance on the next load.
        //
        // A "no" there means "not applicable / not granted in this prompt", not
        // "the visitor declined". Only marketing and analytics are decided by
        // the UI the visitor actually saw.
        const decided = [c.marketing, c.analytics];
        if (decided.some((v) => v === 'no')) return true;
        if (decided.some((v) => v === 'yes')) return false;

        // Neither marketing nor analytics decided. Fall back to sale_of_data,
        // which IS the meaningful signal in CCPA regions (where our own opt-out
        // form writes it) -- but only when it's an explicit value.
        if (c.sale_of_data === 'no') return true;
        if (c.sale_of_data === 'yes') return false;

        return null; // all blank -- undecided
      } catch (e) {
        return null;
      }
    },

    /**
     * Has the visitor opted out of the SALE/SHARING of personal information
     * specifically? This is the CCPA/CPRA question, which is narrower than the
     * general isOptedOut flag.
     *
     * Sources, in order: a standing GPC signal or server-side privacy tag (both
     * are explicit do-not-sell signals), our own opt-out form, then Shopify's
     * sale_of_data record. A GDPR-region analytics/marketing decline does NOT
     * count -- that visitor was never asked about sale of data.
     *
     * @returns {boolean}
     */
    isSaleOfDataOptedOut() {
      if (this.hasGPCSignal) return true;
      if (window.__customerPrivacy && window.__customerPrivacy.hasPrivacyTag) return true;

      // Our own opt-out form / opt-in button record the sale-of-data election
      // directly, so trust a stored decision from those sources.
      const stored = this.getOptOutData();
      if (stored && (stored.source === 'form' || stored.source === 'gpc' ||
                     stored.source === 'server_tag')) {
        return stored.optedOut === true;
      }

      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.currentVisitorConsent === 'function') {
          const c = cp.currentVisitorConsent() || {};
          if (c.sale_of_data === 'yes') return false;
          if (c.sale_of_data === 'no') {
            // "no" is only a real DECLINE where the visitor was actually asked.
            // Shopify surfaces sale-of-data only in sale-of-data regions
            // (CCPA/CPRA states); elsewhere -- Quebec, EU -- it is absent from
            // the prompt and reports "no" for everyone, which would otherwise
            // brand every GDPR visitor as having opted out of a sale they were
            // never asked about.
            let askedAboutSale = false;
            try {
              askedAboutSale = typeof cp.saleOfDataRegion === 'function' &&
                cp.saleOfDataRegion() === true;
            } catch (err) {}
            return askedAboutSale;
          }
        }
      } catch (e) {}

      return false; // no sale-of-data election on record
    },

    /**
     * Write the opt-out record to localStorage WITHOUT pushing to Shopify.
     *
     * setOptOut() calls syncWithShopifyConsent(); this deliberately does not, so
     * it is safe to call while reconciling FROM Shopify (pushing back would just
     * echo Shopify's own value at it, and from inside its consent event that
     * risks a write loop).
     *
     * @param {boolean} optOut
     * @param {string} source
     */
    writeOptOutRecord(optOut, source) {
      this.isOptedOut = optOut;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          optedOut: optOut,
          timestamp: new Date().toISOString(),
          source: source,
          customerId: this.customerId,
          gpcDetected: this.hasGPCSignal
        }));
      } catch (e) {
        console.error('[PrivacyOptOut] Error writing opt-out record:', e);
      }
    },

    /**
     * Mirror consent decisions made in Shopify's own UI into our state.
     *
     * Deliberately does NOT call setOptOut(): that would call
     * syncWithShopifyConsent(), pushing consent back into Shopify from inside
     * Shopify's own consent event. We write localStorage directly and dispatch
     * our change event so TrackingManager still reacts.
     *
     * Note the event detail field is `analyticsAllowed` -- NOT
     * `analyticsProcessingAllowed`, which is the method name on customerPrivacy.
     * We read the detail when present and fall back to currentVisitorConsent()
     * so a payload shape change can't silently swallow a decline.
     */
    bindNativeConsentListener() {
      document.addEventListener('visitorConsentCollected', (e) => {
        // Every decision -- banner, modal, or our own setTrackingConsent push --
        // becomes the latest record that other pages compare against.
        this.rememberShopifyRecord();

        const detail = (e && e.detail) || {};
        let declined = null;

        const flags = [detail.marketingAllowed, detail.analyticsAllowed,
                       detail.saleOfDataAllowed];
        if (flags.some((v) => typeof v === 'boolean')) {
          // Any category actively denied counts as an opt-out for our purposes.
          declined = flags.some((v) => v === false);
        } else {
          // Fall back to Shopify's stored record ('yes' | 'no' | '').
          declined = this.getShopifyDecision();
        }

        // Undeterminable payload -- leave existing state untouched rather than
        // guessing in either direction.
        if (declined === null) {
          warn('[PrivacyOptOut] visitorConsentCollected with no readable consent state; ignoring');
          return;
        }

        // A GPC signal or server tag is a standing opt-out that a banner click
        // must not silently reverse.
        if (!declined && (this.hasGPCSignal ||
            (window.__customerPrivacy && window.__customerPrivacy.hasPrivacyTag))) {
          log('[PrivacyOptOut] Native opt-in ignored; GPC/server-tag opt-out stands');
          return;
        }

        if (this.isOptedOut === declined) return; // no change, stay quiet

        // Covers BOTH Shopify UIs: the native banner's Accept/Decline buttons
        // and the preferences modal (Accept all / Decline all / Save my
        // choices). The modal writes only to Shopify, so this listener is the
        // only thing that mirrors a modal choice into our storage.
        this.writeOptOutRecord(declined, 'native_banner');

        log('[PrivacyOptOut] Native consent decision captured:', { optedOut: declined });
        this.dispatchEvent(OPT_OUT_CHANGE_EVENT, {
          isOptedOut: declined,
          source: 'native_banner'
        });
        this.updatePrivacyUI(); // no-op off the privacy page
      });
    },

    /**
     * @returns {string} 'navigate' | 'reload' | 'back_forward' | 'prerender'
     */
    navigationType() {
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav) return nav.type;
      } catch (e) {}
      return 'navigate';
    },

    /**
     * Shopify's consent record as THIS page sees it, normalized for comparison.
     * @returns {string|null} null while the API isn't available
     */
    readShopifyRecord() {
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.currentVisitorConsent !== 'function') return null;
        const c = cp.currentVisitorConsent() || {};
        return JSON.stringify([c.marketing, c.analytics, c.preferences, c.sale_of_data]);
      } catch (e) {
        return null;
      }
    },

    rememberShopifyRecord() {
      const record = this.readShopifyRecord();
      if (record === null) return;
      try { localStorage.setItem(LATEST_RECORD_KEY, record); } catch (e) {}
    },

    /**
     * Reload this page if its consent state is older than the visitor's
     * latest decision.
     *
     * Two ways a page shows outdated consent, both on Back/Forward:
     *
     *   1. bfcache restore (pageshow persisted=true): the page comes back
     *      exactly as it was left -- banner, tracker blocks, Google consent --
     *      without re-running any script.
     *   2. History load from the HTTP cache (navType back_forward,
     *      persisted=false): when bfcache can't be used, the browser rebuilds
     *      the page from the HTML it originally downloaded, without asking the
     *      server again. Shopify's customerPrivacy API on that page reports the
     *      consent state from that download -- the _tracking_consent cookie
     *      isn't readable from page script on this store, so the API can't
     *      correct itself. Confirmed: Accept on page B, browse on, Back to B ->
     *      currentVisitorConsent all "" (undecided), banner up, Google denied.
     *
     * Neither the page nor Shopify's API can tell it's outdated, so the latest
     * decision is kept in localStorage (shared by every page, always current):
     * written on every fresh load and every decision, never by a history load.
     * A history load or restore that disagrees with it is reloaded from the
     * server, re-running the whole consent stack against the real decision.
     * Pages that already reflect the latest decision don't reload, so Back
     * stays instant whenever nothing changed -- e.g. a bfcache restore of the
     * page the decision was made on, which applied it in place before leaving.
     *
     * Tracking in the moment before the reload can't over-reach: a decline
     * anywhere also writes our privacy_opt_out record, which tracking-manager
     * checks before granting anything.
     *
     * @param {string|null} pageRecord - this page's record (readShopifyRecord())
     * @param {string} trigger - for the debug log
     * @returns {boolean} true if a reload was started
     */
    reloadIfConsentStale(pageRecord, trigger) {
      let latestRecord = null;
      try { latestRecord = localStorage.getItem(LATEST_RECORD_KEY); } catch (e) {}

      const canCompare = pageRecord !== null && latestRecord !== null;
      if (!canCompare || pageRecord === latestRecord) return false;

      log('[PrivacyOptOut] Page consent is older than the latest decision; reloading', {
        trigger, page: pageRecord, latest: latestRecord
      });
      if (DEBUG) {
        // Lets consent-debug.js on the reloaded page report what happened.
        try {
          sessionStorage.setItem('consent_debug_stale_reload',
            JSON.stringify({ href: location.href, at: Date.now(), trigger }));
        } catch (e) {}
      }
      location.reload();
      return true;
    },

    /**
     * Case 1 of reloadIfConsentStale(). The page's record is captured at
     * pagehide -- what this page reflects when it enters the cache -- because
     * a restored page's own API can't be trusted to describe its frozen state.
     */
    reloadStaleBfcacheRestores() {
      let recordAtHide = null;
      window.addEventListener('pagehide', () => {
        recordAtHide = this.readShopifyRecord();
      });
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        this.reloadIfConsentStale(recordAtHide, 'bfcache restore');
      });
    },

    /**
     * Get Shopify customer privacy opt-out status
     * Checks if customer has opted out via Shopify's customerPrivacy API
     * @returns {boolean}
     */
    getShopifyOptOutStatus() {
      // Delegates to getShopifyDecision() so there is exactly ONE place that
      // interprets Shopify's consent record.
      //
      // This used to check saleOfDataAllowed() === false first and return true
      // immediately. In a GDPR-style region that is always false (there's no
      // sale-of-data toggle in Shopify's prompt there), so every Quebec/EU
      // visitor read as "opted out" regardless of what they actually chose --
      // which is what reverted "Accept all" and left the privacy page showing
      // "You have opted out".
      //
      // @returns {boolean} true only when there is an EXPLICIT opt-out;
      //   an undecided visitor is not opted out.
      return this.getShopifyDecision() === true;
    },

    /**
     * Check if user is logged in
     * @returns {boolean}
     */
    isLoggedIn() {
      return window.__customerPrivacy && window.__customerPrivacy.isLoggedIn === true;
    },

    /**
     * Get customer info from Liquid template
     * @returns {Object|null}
     */
    getCustomerInfo() {
      if (window.__customerPrivacy && window.__customerPrivacy.isLoggedIn) {
        return {
          id: window.__customerPrivacy.customerId,
          email: window.__customerPrivacy.email,
          firstName: window.__customerPrivacy.firstName,
          lastName: window.__customerPrivacy.lastName
        };
      }
      return null;
    },

    /**
     * Get current opt-out status from localStorage
     * @returns {boolean}
     */
    getOptOutStatus() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          return data.optedOut === true;
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error reading opt-out status:', e);
      }
      return false;
    },

    /**
     * Get full opt-out data from localStorage
     * @returns {Object|null}
     */
    getOptOutData() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        console.error('[PrivacyOptOut] Error reading opt-out data:', e);
      }
      return null;
    },

    /**
     * Set opt-out status
     * @param {boolean} optOut - Whether to opt out
     * @param {string} source - Source of the opt-out (form, gpc, api)
     * @returns {boolean} Success status
     */
    setOptOut(optOut, source = 'form') {
      try {
        const data = {
          optedOut: optOut,
          timestamp: new Date().toISOString(),
          source: source,
          customerId: this.customerId,
          gpcDetected: this.hasGPCSignal
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        this.isOptedOut = optOut;

        // Sync with Shopify consent API
        this.syncWithShopifyConsent();

        // Dispatch change event
        this.dispatchEvent(OPT_OUT_CHANGE_EVENT, {
          isOptedOut: this.isOptedOut,
          source: source
        });

        log('[PrivacyOptOut] Opt-out status updated:', data);
        return true;
      } catch (e) {
        console.error('[PrivacyOptOut] Error setting opt-out status:', e);
        return false;
      }
    },

    /**
     * Clear opt-out status (opt back in)
     * @returns {boolean} Success status
     */
    clearOptOut() {
      return this.setOptOut(false, 'form');
    },

    /**
     * Sync our opt-out state to Shopify's customerPrivacy API.
     *
     * NOTE: this writes marketing/analytics/sale_of_data = !isOptedOut, so
     * calling it while isOptedOut === false PUSHES AN OPT-IN. That is only ever
     * valid off an explicit user action (accept/opt-in), never as a load-time
     * default — see the gated call in init(). setOptOut() calls this directly
     * for both directions, which is correct because setOptOut() only runs from a
     * real decision (banner/form) or a genuine opt-out (GPC/tag).
     */
    syncWithShopifyConsent(retryCount = 0) {
      const MAX_RETRIES = 5;
      const RETRY_DELAY = 500; // ms

      if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
        try {
          // Set tracking consent including sale_of_data
          if (typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
            window.Shopify.customerPrivacy.setTrackingConsent({
              marketing: !this.isOptedOut,
              analytics: !this.isOptedOut,
              // Follows the opt-out like every other category. Preference
              // cookies are NOT strictly necessary under GDPR -- they're a
              // consentable category -- so forcing them true during an opt-out
              // both overrode Shopify's own Decline (which sets preferences:no)
              // and left "Personalization" checked in the preferences modal
              // after the visitor had declined everything.
              preferences: !this.isOptedOut,
              sale_of_data: !this.isOptedOut
            }, () => {
              log('[PrivacyOptOut] Shopify setTrackingConsent completed:', {
                sale_of_data: !this.isOptedOut,
                marketing: !this.isOptedOut,
                analytics: !this.isOptedOut
              });

              // Verify the consent was set
              this.verifyShopifyConsent();
            });
          }
        } catch (e) {
          console.error('[PrivacyOptOut] Error syncing with Shopify consent:', e);
        }
      } else if (retryCount < MAX_RETRIES) {
        warn('[PrivacyOptOut] Shopify customerPrivacy API not available, retrying (' + (retryCount + 1) + '/' + MAX_RETRIES + ')...');
        setTimeout(() => {
          this.syncWithShopifyConsent(retryCount + 1);
        }, RETRY_DELAY * (retryCount + 1));
      } else {
        console.error('[PrivacyOptOut] Shopify customerPrivacy API not available after ' + MAX_RETRIES + ' retries');
      }
    },

    /**
     * Verify Shopify consent state (for debugging)
     */
    verifyShopifyConsent() {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
        try {
          const saleAllowed = window.Shopify.customerPrivacy.saleOfDataAllowed?.() ?? 'N/A';
          const currentConsent = window.Shopify.customerPrivacy.currentVisitorConsent?.() ?? {};
          const region = window.Shopify.customerPrivacy.getRegion?.() ?? 'N/A';
          const inSaleRegion = window.Shopify.customerPrivacy.saleOfDataRegion?.() ?? 'N/A';

          log('[PrivacyOptOut] Shopify consent verification:', {
            saleOfDataAllowed: saleAllowed,
            currentVisitorConsent: currentConsent,
            region: region,
            saleOfDataRegion: inSaleRegion
          });
        } catch (e) {
          console.error('[PrivacyOptOut] Error verifying Shopify consent:', e);
        }
      }
    },

    /**
     * Dispatch custom event
     * @param {string} eventName - Event name
     * @param {Object} detail - Event detail
     */
    dispatchEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        bubbles: true,
        detail: detail
      });
      document.dispatchEvent(event);
    },

    /**
     * Bind form handlers for privacy choices page
     */
    bindFormHandlers() {
      // Opt-out form handler. Guarded per-element: init() runs twice on the
      // privacy page (see init()), and a duplicate submit listener would fire
      // handleOptOutSubmit() twice -- two setOptOut() writes and, worse, two
      // POSTs to the customer-tagging app proxy.
      const optOutForm = document.getElementById('privacy-opt-out-form');
      if (optOutForm && !optOutForm.dataset.privacyBound) {
        optOutForm.dataset.privacyBound = '1';
        optOutForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.handleOptOutSubmit(optOutForm);
        });

        // Pre-fill form with customer data if logged in
        this.prefillFormWithCustomerData(optOutForm);
      }

      // Opt-in button handler
      const optInBtn = document.getElementById('privacy-opt-in-btn');
      if (optInBtn && !optInBtn.dataset.privacyBound) {
        optInBtn.dataset.privacyBound = '1';
        optInBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleOptIn();
        });
      }

      // Update UI based on current status
      this.updatePrivacyUI();
    },

    /**
     * Pre-fill form fields with customer data if logged in
     * @param {HTMLFormElement} form
     */
    prefillFormWithCustomerData(form) {
      const customer = this.getCustomerInfo();
      if (!customer) return;

      // Pre-fill email field
      const emailField = form.querySelector('input[name="email"], #opt-out-email');
      if (emailField && customer.email && !emailField.value) {
        emailField.value = customer.email;
      }

      // Pre-fill name field
      const nameField = form.querySelector('input[name="name"], #opt-out-name');
      if (nameField && (customer.firstName || customer.lastName) && !nameField.value) {
        const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
        nameField.value = fullName;
      }

      log('[PrivacyOptOut] Form pre-filled with customer data');
    },

    /**
     * Tag customer account via app proxy endpoint
     * @param {string} email - Customer email
     */
    tagCustomerAccount(email) {
      try {
        fetch(`/apps/dg-connection/check-opt-out?email=${encodeURIComponent(email)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        .then(response => {
          if (response.ok) {
            log('[PrivacyOptOut] Customer account tagged via app proxy');
          } else {
            warn('[PrivacyOptOut] App proxy responded with status:', response.status);
          }
        })
        .catch(err => {
          console.error('[PrivacyOptOut] Error tagging customer account:', err);
        });
      } catch (e) {
        console.error('[PrivacyOptOut] Error calling app proxy:', e);
      }
    },

    /**
     * Handle opt-out form submission
     * @param {HTMLFormElement} form
     */
    handleOptOutSubmit(form) {
      const formData = new FormData(form);
      const email = formData.get('email');
      const name = formData.get('name');

      // Validate email
      if (!this.validateEmail(email)) {
        this.showMessage('error', 'Please enter a valid email address.');
        return;
      }

      // Set opt-out in localStorage and sync with Shopify
      const success = this.setOptOut(true, 'form');

      if (success) {
        // Store additional info for potential backend sync
        try {
          const requestData = {
            ...this.getOptOutData(),
            email: email,
            name: name || ''
          };
          localStorage.setItem(STORAGE_KEY + '_request', JSON.stringify(requestData));
        } catch (e) {
          console.error('[PrivacyOptOut] Error storing request data:', e);
        }

        // Tag customer account via app proxy
        this.tagCustomerAccount(email);

        // Log what was updated for verification
        log('[PrivacyOptOut] Opt-out submitted:', {
          email: email,
          localStorage: this.getOptOutData(),
          shopifyConsentSynced: typeof window.Shopify !== 'undefined' && !!window.Shopify.customerPrivacy
        });

        this.showMessage('success', 'Your opt-out preference has been saved. We will no longer sell or share your personal information for targeted advertising.');
        this.updatePrivacyUI();
      } else {
        this.showMessage('error', 'There was an error saving your preference. Please try again.');
      }
    },

    /**
     * Handle opt-in action
     */
    handleOptIn() {
      const success = this.clearOptOut();

      if (success) {
        this.showMessage('success', 'Your preference has been updated. You have opted back in to personalized advertising.');
        this.updatePrivacyUI();
      } else {
        this.showMessage('error', 'There was an error updating your preference. Please try again.');
      }
    },

    /**
     * Update privacy page UI based on current status
     */
    updatePrivacyUI() {
      const statusBanner = document.getElementById('privacy-status-banner');
      const optOutSection = document.getElementById('privacy-opt-out-section');
      const optInSection = document.getElementById('privacy-opt-in-section');
      const gpcNotice = document.getElementById('privacy-gpc-notice');

      // This page is specifically the CCPA/CPRA "Do Not Sell or Share" page, so
      // its status must reflect SALE-OF-DATA -- not the general isOptedOut flag.
      //
      // Those are different questions. A Quebec visitor who declines analytics
      // in Shopify's GDPR prompt is "opted out" for tracking purposes, but has
      // made no sale-of-data election at all -- Shopify's prompt in that region
      // has no such toggle. Rendering "You have opted out of the sale/sharing of
      // your personal information" off a GDPR analytics decline told visitors
      // something that was never asked and isn't true.
      const saleOptedOut = this.isSaleOfDataOptedOut();

      // Build status message
      const customer = this.getCustomerInfo();
      const loggedInText = customer ? ` (Signed in as ${customer.email})` : '';

      if (statusBanner) {
        if (saleOptedOut) {
          statusBanner.classList.add('opted-out');
          statusBanner.classList.remove('opted-in');
          statusBanner.innerHTML = `<p><strong>Status:</strong> You have opted out of the sale/sharing of your personal information.${loggedInText}</p>`;
        } else {
          statusBanner.classList.add('opted-in');
          statusBanner.classList.remove('opted-out');
          statusBanner.innerHTML = `<p><strong>Status:</strong> You have not opted out of the sale/sharing of your personal information.${loggedInText}</p>`;
        }
      }

      if (optOutSection) {
        optOutSection.style.display = saleOptedOut ? 'none' : 'block';
      }

      if (optInSection) {
        optInSection.style.display = saleOptedOut ? 'block' : 'none';
      }

      if (gpcNotice) {
        gpcNotice.style.display = this.hasGPCSignal ? 'block' : 'none';
        gpcNotice.setAttribute('aria-hidden', this.hasGPCSignal ? 'false' : 'true');
      }
    },

    /**
     * Show message to user
     * @param {string} type - Message type (success, error)
     * @param {string} message - Message text
     */
    showMessage(type, message) {
      const container = document.getElementById('privacy-message');
      if (container) {
        container.className = 'privacy-message privacy-message--' + type;
        container.textContent = message;
        container.style.display = 'block';
        container.setAttribute('role', 'alert');

        // Auto-hide after 5 seconds for success messages
        if (type === 'success') {
          setTimeout(() => {
            container.style.display = 'none';
          }, 5000);
        }
      }
    },

    /**
     * Validate email format
     * @param {string} email
     * @returns {boolean}
     */
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(String(email).toLowerCase());
    },

    /**
     * Open cookie consent modal (if available)
     */
    openCookieConsent() {
      let shown = false;

      // Try Shopify's privacyBanner.showPreferences() first (this is the correct method)
      if (typeof window.privacyBanner !== 'undefined' &&
          typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
        log('[PrivacyOptOut] Opened Shopify privacy banner preferences');
        shown = true;
      }
      // Fallback: Try window.Shopify.privacyBanner
      else if (typeof window.Shopify !== 'undefined' &&
               window.Shopify.privacyBanner &&
               typeof window.Shopify.privacyBanner.showPreferences === 'function') {
        window.Shopify.privacyBanner.showPreferences();
        log('[PrivacyOptOut] Opened Shopify.privacyBanner preferences');
        shown = true;
      }
      // Fallback: Try customerPrivacy.showPreferences if it exists
      else if (typeof window.Shopify !== 'undefined' &&
               window.Shopify.customerPrivacy &&
               typeof window.Shopify.customerPrivacy.showPreferences === 'function') {
        window.Shopify.customerPrivacy.showPreferences();
        log('[PrivacyOptOut] Opened customerPrivacy preferences');
        shown = true;
      }

      // Fallback to theme's custom cookie consent banner
      if (!shown) {
        const cookieConsent = document.querySelector('.cookie_consent');
        if (cookieConsent) {
          cookieConsent.style.display = 'block';
          log('[PrivacyOptOut] Opened theme cookie consent banner');
          shown = true;
        }
      }

      // If nothing available, log available objects for debugging
      if (!shown) {
        warn('[PrivacyOptOut] No cookie consent UI available. Checking what exists:');
        log('  - window.privacyBanner:', typeof window.privacyBanner);
        log('  - window.Shopify.privacyBanner:', typeof window.Shopify?.privacyBanner);
        log('  - window.Shopify.customerPrivacy:', typeof window.Shopify?.customerPrivacy);
        this.showMessage('error', 'Cookie preferences panel is not available. Please check your browser cookie settings or contact us for assistance.');
      }
    }
  };

  // Expose to global scope
  window.PrivacyOptOut = PrivacyOptOut;
})();
